/**
 * Textos (`es`) de las fichas técnicas de proveedor, fase 1 (OBR-41): adjuntarlas, solicitarlas y la vista de
 * «FT pendientes por proveedor». Corte propio del Catálogo: solo se edita este fichero (el cableado está en
 * `cataleg/es.ts`). Las tres lenguas deben tener las mismas claves (i18n.test.ts); ningún texto literal en las vistas.
 *
 * `emailSubjectDefault` y `emailBodyDefault` son las PLANTILLAS del correo: se renderizan en el idioma del proveedor
 * (el de los pedidos y, si no tiene, el de quien solicita) y la persona puede editarlas antes de enviarlas.
 */

export const ftRequestEs = {
  'backoffice.ftRequest': {
    // ── Estado ───────────────────────────────────────────────────────────────────────────
    statusNone: 'Sin FT',
    statusRequested: 'Solicitada',
    statusValid: 'Vigente',

    // ── El bloque de una relación artículo-proveedor ─────────────────────────────────────
    blockTitle: 'Ficha técnica del proveedor',
    noneHint: 'Todavía no se ha adjuntado ni solicitado la ficha técnica de este proveedor.',
    requestedSummary: 'Solicitada el {{date}} a {{recipient}}.',
    requestedSummaryNoRecipient: 'Solicitada el {{date}}.',
    validDocument: 'Documento del {{date}} · versión {{version}}',
    validAttached: 'Incorporada el {{date}} por {{name}}.',
    validAttachedNoName: 'Incorporada el {{date}}.',
    legacyHint:
      'Adjuntada antes de registrar su fecha y su versión: no se conocen. Adjúntala de nuevo o complétalas para dejar constancia.',
    openFile: 'Abre la FT',
    attachAction: 'Adjunta FT',
    replaceAction: 'Sustituye FT',
    completeAction: 'Completa fecha y versión',
    requestAction: 'Solicita FT',
    requestAgainAction: 'Volver a solicitar',
    saveFirstHint: 'Guarda el artículo para adjuntar o solicitar la FT de este proveedor.',
    supplierChangedHint:
      'Has cambiado el proveedor de esta relación: guarda el artículo antes de adjuntar o solicitar la FT.',
    supplierInactiveHint: 'Este proveedor está de baja: no se le puede solicitar la FT.',
    loadError: 'No se ha podido leer el estado de las FT de este artículo.',

    // ── Adjuntar ─────────────────────────────────────────────────────────────────────────
    attachTitle: 'Adjunta la FT de {{supplier}}',
    attachIntro:
      'La ficha técnica del proveedor para {{article}}. Hace falta la fecha que lleva el documento y su versión.',
    fieldFile: 'Fichero (PDF)',
    fieldFileHint: 'PDF de hasta 10 MB.',
    keepFile: 'Mantén el fichero actual y solo corrige la fecha y la versión',
    fieldDocumentDate: 'Fecha del documento',
    fieldDocumentDateHint: 'La que lleva la ficha del proveedor, no la de hoy.',
    fieldVersion: 'Versión',
    fieldVersionHint: 'Tal como la indica el proveedor (p. ej. «v3» o «2026-05»).',
    attachConfirm: 'Adjunta',
    attachError: 'No se ha podido adjuntar la FT. Vuelve a intentarlo.',
    attachForbidden: 'No tienes permiso para adjuntar fichas técnicas.',
    attachNotFound: 'Esta relación artículo-proveedor ya no existe. Recarga la página.',
    attachSuccess: 'FT adjuntada.',
    attachSaved: 'Datos de la FT guardados.',
    errorFileRequired: 'Elige el fichero PDF de la FT.',
    errorDateRequired: 'Escribe la fecha del documento.',
    errorDateInvalid: 'La fecha del documento no es válida.',
    errorDateFuture: 'La fecha del documento no puede ser futura.',
    errorVersionRequired: 'Escribe la versión del documento.',
    errorVersionTooLong: 'La versión es demasiado larga (máximo {{max}} caracteres).',

    // ── Solicitar ────────────────────────────────────────────────────────────────────────
    requestTitle: 'Solicita la FT a {{supplier}}',
    requestIntro:
      'Se enviará un correo desde Obrava al proveedor. Puedes editar el asunto y el texto; la lista de artículos se añade sola.',
    recipientLabel: 'Destinatario',
    recipientQuality: '{{email}} (correo de calidad)',
    recipientGeneral: '{{email}} (correo general: el proveedor no tiene correo de calidad)',
    noRecipientWarning:
      'Este proveedor no tiene ningún correo electrónico. Añade uno en su ficha (Configuración · Proveedores) para poder solicitarle la FT: no se enviará nada.',
    fieldSubject: 'Asunto',
    fieldBody: 'Texto',
    articlesLabel: 'Artículos incluidos ({{count}})',
    articleLine: '{{name}}',
    articleLineRef: '{{name}} · ref. {{ref}}',
    requestConfirm: 'Envía la solicitud',
    requestSuccess: 'Solicitud enviada a {{recipient}}.',
    errorNoRecipient:
      'Este proveedor no tiene ningún correo electrónico: no se ha enviado nada. Añade uno en su ficha.',
    errorNoArticles: 'Marca al menos un artículo.',
    errorTooManyArticles: 'Una petición admite como máximo {{max}} artículos.',
    errorSubjectRequired: 'Escribe el asunto.',
    errorSubjectTooLong: 'El asunto es demasiado largo (máximo {{max}} caracteres).',
    errorBodyRequired: 'Escribe el texto del mensaje.',
    errorBodyTooLong: 'El texto es demasiado largo (máximo {{max}} caracteres).',
    errorForbidden: 'No tienes permiso para solicitar fichas técnicas.',
    errorSupplierNotFound: 'No se ha encontrado el proveedor.',
    errorArticlesNotLinked:
      'Alguno de los artículos ya no es de este proveedor. Cierra la ventana, revisa la lista y vuelve a intentarlo.',
    errorAlreadyHasFt:
      'Alguno de los artículos ya tiene la FT adjuntada. Cierra la ventana, revisa la lista y vuelve a intentarlo.',
    errorSmtpNotConfigured: 'El envío de correo no está configurado. Avisa al administrador de Obrava.',
    errorRegistryUnavailable:
      'El registro de solicitudes no está disponible: no se ha enviado nada. Avisa al administrador de Obrava.',
    errorRecentlyRequested:
      'Ya se ha solicitado la FT a este proveedor hace poco: no se le puede volver a escribir hasta pasados unos minutos.',
    errorDailyLimit:
      'Se ha alcanzado el límite de solicitudes de FT de las últimas 24 horas. Vuelve a intentarlo más tarde.',
    limitCooldown:
      'Ya se ha solicitado la FT a este proveedor hace poco. Para que no reciba correos repetidos, no se le puede volver a escribir hasta dentro de {{minutes}} min.',
    limitDaily:
      'El obrador ha alcanzado el límite de {{max}} solicitudes de FT en 24 horas. Se podrá volver a solicitar dentro de {{hours}} h.',
    fileBadUrl: 'La dirección del fichero no es válida.',
    errorSendFailed: 'No se ha podido enviar el correo. No se ha registrado nada: puedes volver a intentarlo.',
    errorRecordFailed:
      'El correo se ha enviado, pero no se ha podido registrar la solicitud. No lo vuelvas a enviar.',
    errorTryLater: 'No se ha podido contactar con el servidor. Vuelve a intentarlo dentro de un momento.',
    errorGeneric: 'No se ha podido enviar la solicitud.',

    // ── La plantilla del correo (se renderiza en el idioma del proveedor) ────────────────
    emailSubjectDefault: 'Solicitud de ficha técnica · {{obrador}}',
    emailBodyDefault:
      'Buenos días,\n\nPara completar nuestro registro de materias primas y garantizar su trazabilidad, necesitamos la ficha técnica vigente de los artículos siguientes. Os agradeceremos que respondáis a este correo adjuntándola en PDF, con la fecha y la versión del documento.\n\nMuchas gracias.',

    // ── FT pendientes por proveedor ──────────────────────────────────────────────────────
    pendingButton: 'FT pendientes',
    pendingTitle: 'FT pendientes por proveedor',
    pendingIntro:
      'Los artículos que todavía no tienen la ficha técnica del proveedor, agrupados por proveedor. Cada proveedor recibe una sola petición con todos los artículos que marques.',
    pendingTotals: 'Proveedores: {{suppliers}} · Artículos sin FT: {{articles}}',
    pendingLoading: 'Cargando las FT pendientes…',
    pendingLoadError: 'No se han podido cargar las FT pendientes.',
    pendingEmpty: 'No hay ninguna FT pendiente: todos los artículos con proveedor tienen la ficha técnica.',
    colArticle: 'Artículo',
    colRef: 'Ref. proveedor',
    colStatus: 'Estado',
    groupMeta: 'Sin FT: {{count}}',
    groupNoRecipient: 'Sin correo',
    groupNoRecipientHint:
      'Este proveedor no tiene ningún correo electrónico: añade uno en su ficha para poder solicitarle la FT.',
    groupRecipient: 'A: {{recipient}}',
    groupRequestAction: 'Solicita FT ({{count}})',
    groupSelectNone: 'Marca al menos un artículo para solicitarlo.',
    groupSelectAll: 'Marca todos los artículos de {{supplier}}',
    itemSelect: 'Incluye {{article}} en la petición',
    itemRequestedOn: 'Solicitada el {{date}}',
    pendingClose: 'Cerrar',
  },
} as const;
